import { v4 as uuidv4 } from "uuid";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import Interaction, { InteractionType } from "./Interaction";
import {
  InteractionProperty,
  InteractionPropertyType,
} from "../InteractionProperty";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import Take from "../Take";
import { Actor } from "../Actor";
import SetCameraPreview from "../../features/previews/set_camera/SetCameraPreview";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import SetCameraNodePreview from "../../features/sequence/node_previews/SetCameraNodePreview";
import { compactSearchableStrings } from "./SearchableInteractionValues";
import { InstantSpeedPropertiesEditor } from "../../features/interaction_properties_column/editors/InstantSpeedPropertiesEditor";

export interface ISetCamera extends Interaction {
  cameraId: string;
  takeLocationId: string;
  isSelecting?: boolean;
}

export const requiredCameraProperties: InteractionProperty[] = [
  {
    id: "instant",
    name: "instant",
    locId: "instant",
    type: InteractionPropertyType.boolean,
    value: false,
  },
  {
    id: "speed",
    name: "Speed",
    locId: "speed",
    type: InteractionPropertyType.number,
    value: 1,
  },
  {
    id: "WaitUntilFinished",
    name: "Wait until finished",
    type: InteractionPropertyType.boolean,
    value: true,
  },
];

export function CreateSetCamera(camera?: ISetCamera): ISetCamera {
  return {
    id: uuidv4(),
    name: "Main Camera",
    cameraId: camera ? camera.cameraId : "",
    type: InteractionType.SetCamera,
    properties: camera ? camera.properties : requiredCameraProperties,
  } as ISetCamera;
}

export function MigrateSetCamera(
  from: number,
  to: number,
  interaction: ISetCamera
): ISetCamera {
  if (interaction.properties === undefined)
    return { ...interaction, properties: requiredCameraProperties };

  // drill the props and add any missing ones
  var newProperties: InteractionProperty[] = GetMigratedProperties(
    requiredCameraProperties,
    interaction.properties
  );
  console.log(
    "[SetCamera] migrated " +
      (newProperties.length - interaction.properties.length) +
      " properties"
  );

  return { ...interaction, properties: newProperties };
}

export const setCameraInteraction = (): InteractionFactoryEntity => {
  const view = <SetCameraPreview />;

  const filterType: string = "Set Camera";
  const create = (getKeys: (amount: number) => string[]): ISetCamera => {
    return {
      id: uuidv4(),
      type: InteractionType.SetCamera,
      name: "Set Camera",
      cameraId: "",
      takeLocationId: "",
      properties: requiredCameraProperties,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): Interaction => {
    if (interaction.properties === undefined)
      return { ...interaction, properties: requiredCameraProperties };

    // drill the props and add any missing ones
    var newProperties: InteractionProperty[] = GetMigratedProperties(
      requiredCameraProperties,
      interaction.properties
    );
    console.log(
      "[SetCamera] migrated " +
        (newProperties.length - interaction.properties.length) +
        " properties"
    );

    return { ...interaction, properties: newProperties };
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[]
  ) => {
    const converted = interaction as ISetCamera;
    return {
      id: uuidv4(),
      type: InteractionType.SetCamera,
      name: converted.name,
      cameraId: converted.cameraId,
      takeLocationId: converted.takeLocationId,
      properties: converted.properties ? [...converted.properties] : [],
    } as ISetCamera;
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    currentTake: Take,
    actorPresets: Actor[]
  ) => {
    const converted = interaction as ISetCamera;
    const warnings: string[] = [];

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[]
  ) => {
    const converted = interaction as ISetCamera;
    const searchableStrings = compactSearchableStrings(
      converted.name,
      converted.cameraId,
      converted.takeLocationId,
    );

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  const validateError = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[]
  ): string[] => {
    const converted = interaction as ISetCamera;
    const errors: string[] = [];

    if (converted.cameraId === "") errors.push("Camera ID is required");

    return errors;
  };

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <SetCameraNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    PropertiesEditor: InstantSpeedPropertiesEditor,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    FilterType: filterType,
    Filter: filter,
    ValidateErrors: validateError,
  };
};
